/**
 * Client de API para Comunicação com a HostGator (MySQL + PHP)
 * Fornece autenticação, sincronização em tempo real de múltiplos perfis,
 * playlists e histórico de reprodução.
 */

import { UserProfile, PlaylistSource, AllCategoriesConfig, ServerDNS, SystemClient, PlaylistCredential, ClientSubscription, ActiveScreen, HeartbeatResponse, PlaybackLogItem, RouteSummary, TelemetryStats, TelemetryErrorItem } from '../types/iptv';

export interface AuthUser {
  id: number;
  username: string;
  email?: string | null;
  role: 'admin' | 'user';
  server_user?: string | null;
  server_pass?: string | null;
  max_screens?: number;
  max_profiles?: number;
  status?: 'active' | 'inactive';
  created_at?: string;
  last_login?: string;
}

export interface ChannelStreamProfile {
  preferred_player: 'auto' | 'native' | 'hls_mse' | 'mpegts';
  preferred_format: 'auto' | 'm3u8' | 'ts';
  updated_at?: string;
}

export interface SyncAllDataResponse {
  success: boolean;
  profiles: UserProfile[];
  playlists: PlaylistSource[];
  globalCatalog?: {
    channels?: any[];
    movies?: any[];
    series?: any[];
    playlists?: any[];
  } | null;
  syncData: Record<string, Record<string, any>>;
  systemDNS?: ServerDNS[];
  serverCredentials?: {
    server_user?: string | null;
    server_pass?: string | null;
    max_screens: number;
    max_profiles: number;
    status: string;
    role: string;
  };
  serverTime: string;
}

const TOKEN_KEY = 'nexus_auth_token';
const USER_KEY = 'nexus_auth_user';
const DNS_STORAGE_KEY = 'nexus_system_dns_list';
const CLIENTS_STORAGE_KEY = 'nexus_admin_clients_list';
const SESSION_TOKEN_KEY = 'nexus_session_token';
const SESSION_ID_KEY = 'nexus_session_id';

// Obter URL base da API
function getApiBaseUrl(): string {
  if (typeof window === 'undefined') return '/api';
  return '/api';
}

class ApiClient {
  private token: string | null = null;
  private user: AuthUser | null = null;
  private sessionToken: string | null = null;
  private sessionId: number | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem(TOKEN_KEY);
      this.sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
      this.sessionId = parseInt(localStorage.getItem(SESSION_ID_KEY) || '0') || null;
      const savedUser = localStorage.getItem(USER_KEY);
      if (savedUser) {
        try {
          this.user = JSON.parse(savedUser);
        } catch {
          this.user = null;
        }
      }
    }
  }

  public getToken(): string | null {
    return this.token;
  }

  public getUser(): AuthUser | null {
    return this.user;
  }

  public getSessionToken(): string | null {
    return this.sessionToken;
  }

  public getSessionId(): number | null {
    return this.sessionId;
  }

  public isAuthenticated(): boolean {
    return !!this.token && !!this.user;
  }

  public isAdmin(): boolean {
    return this.user?.role === 'admin';
  }

  public setSession(token: string, user: AuthUser, sessionToken?: string, sessionId?: number) {
    this.token = token;
    this.user = user;
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (sessionToken) {
      this.sessionToken = sessionToken;
      localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
    }
    if (sessionId) {
      this.sessionId = sessionId;
      localStorage.setItem(SESSION_ID_KEY, String(sessionId));
    }
  }

  public clearSession() {
    this.token = null;
    this.user = null;
    this.sessionToken = null;
    this.sessionId = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem(SESSION_ID_KEY);
  }

  private async request<T = any>(
    endpoint: string,
    options: {
      method?: 'GET' | 'POST';
      body?: any;
      params?: Record<string, string>;
    } = {}
  ): Promise<T> {
    const { method = 'GET', body, params } = options;

    let url = `${getApiBaseUrl()}/${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += (url.includes('?') ? '&' : '?') + searchParams.toString();
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
      // Token enviado APENAS no header Authorization (não mais na URL para evitar exposição em logs/cache)
    }

    if (this.sessionToken && !endpoint.includes('action=login')) {
      headers['X-Session-Token'] = this.sessionToken;
    }

    if (typeof window !== 'undefined') {
      const screenDevId = this.getScreenDeviceId();
      if (screenDevId) {
        headers['X-Device-Id'] = screenDevId;
      }
    }

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    let data: any = {};
    try {
      data = await response.json();
    } catch {
      data = { error: `HTTP ${response.status}` };
    }

    if (!response.ok) {
      const isAuthExempt = endpoint.includes('action=login') || 
                           endpoint.includes('action=logout') || 
                           endpoint.includes('action=unregister');

      if ((response.status === 401 || data?.session_revoked) && !isAuthExempt && this.token) {
        this.clearSession();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: data }));
        }
      } else if (data?.profile_kicked && !isAuthExempt) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('profile:kicked', { detail: data }));
        }
      } else if (data?.screen_kicked) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('screen:kicked', { detail: data }));
        }
      }
      const err: any = new Error(data.error || `HTTP Error ${response.status}`);
      err.data = data;
      err.status = response.status;
      throw err;
    }

    return data;
  }

  private inMemoryTabId: string = typeof window !== 'undefined'
    ? `scr_${Math.random().toString(36).substring(2, 9)}_${Date.now().toString(36)}`
    : 'srv';

  public getPhysicalDeviceId(): string {
    let devId = 'unknown';
    if (typeof window !== 'undefined') {
      devId = localStorage.getItem('iptv_device_id') || localStorage.getItem('nexus_device_id') || '';
      if (!devId) {
        devId = `dev_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        localStorage.setItem('iptv_device_id', devId);
      }
    }
    return devId;
  }

  public getScreenDeviceId(): string {
    const devId = this.getPhysicalDeviceId();
    if (typeof window !== 'undefined') {
      return `${devId}_${this.inMemoryTabId}`;
    }
    return devId;
  }

  // 1. AUTENTICAÇÃO COM SUPORTE PHP E RETORNO IMEDIATO DE PERFIS
  public async login(
    loginIdentifier: string, 
    password: string,
    extra?: { revoke_session_id?: number; revoke_all_other?: boolean }
  ): Promise<{ token: string; user: AuthUser; profiles?: any[] }> {
    const cleanId = loginIdentifier.trim();
    const cleanPass = password.trim();
    const devId = this.getPhysicalDeviceId();

    try {
      const data = await this.request('auth.php?action=login', {
        method: 'POST',
        body: { 
          username: cleanId, 
          email: cleanId, 
          password: cleanPass,
          device_id: devId,
          revoke_session_id: extra?.revoke_session_id,
          revoke_all_other: extra?.revoke_all_other
        },
      });

      if (data.success && data.token && data.user) {
        this.setSession(data.token, data.user, data.session_token, data.session_id);
        return data;
      }
      throw new Error(data.error || 'Falha no login');
    } catch (err: any) {
      throw err;
    }
  }

  public async register(username: string, password: string, email?: string): Promise<{ token: string; user: AuthUser; profiles?: any[] }> {
    const cleanUser = username.trim();
    const cleanPass = password.trim();
    const cleanEmail = email?.trim();

    try {
      const data = await this.request('auth.php?action=register', {
        method: 'POST',
        body: { username: cleanUser, password: cleanPass, email: cleanEmail },
      });

      if (data.success && data.token && data.user) {
        this.setSession(data.token, data.user);
        return data;
      }
      throw new Error(data.error || 'Falha no cadastro');
    } catch (err: any) {
      throw err;
    }
  }

  public async checkSession(): Promise<AuthUser | null> {
    const res = await this.checkSessionWithProfiles();
    return res ? res.user : null;
  }

  public async checkSessionWithProfiles(): Promise<{ user: AuthUser; profiles?: any[] } | null> {
    if (!this.token) {
      this.clearSession();
      return null;
    }
    try {
      const data = await this.request('auth.php?action=check');
      if (data && data.success && data.user) {
        this.user = data.user;
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        return { user: data.user, profiles: data.profiles || [] };
      }
      this.clearSession();
      return null;
    } catch {
      // Se a sessão for inválida ou foi revogada, desconecta imediatamente
      this.clearSession();
      return null;
    }
  }

  public async logout(): Promise<void> {
    // Notificar o servidor que a sessão foi encerrada manualmente
    if (this.token && this.sessionToken) {
      try {
        await this.request('sessions.php?action=logout', {
          method: 'POST',
          body: { session_token: this.sessionToken },
        });
      } catch {
        // Silencioso — limpar localmente mesmo se a API falhar
      }
    }
    this.clearSession();
  }

  // 2. SINCRONIZAÇÃO EM NUVEM
  public async loadAllUserData(options?: {
    includeCatalog?: boolean;
    catalogVersion?: string;
    slimCatalog?: boolean;
  }): Promise<(SyncAllDataResponse & { catalogVersion?: string; catalogModified?: boolean }) | null> {
    if (!this.isAuthenticated()) return null;
    try {
      const includeCatalog = options?.includeCatalog !== false;
      const params = new URLSearchParams();
      params.set('action', 'load_all');
      if (!includeCatalog) params.set('include_catalog', '0');
      if (options?.catalogVersion) params.set('catalog_version', options.catalogVersion);
      if (options?.slimCatalog) params.set('slim_catalog', '1');
      return await this.request<any>(`sync.php?${params.toString()}`);
    } catch (e) {
      console.warn('[ApiClient] Failed to load cloud data:', e);
      return null;
    }
  }

  public async getVodDetails(id: string, type: 'movie' | 'series', playlistId?: string): Promise<{ success: boolean; id: string; type: string; details?: any } | null> {
    try {
      const params = new URLSearchParams();
      params.set('action', 'get_vod_details');
      params.set('id', id);
      params.set('type', type);
      if (playlistId) params.set('playlist_id', playlistId);
      return await this.request<any>(`sync.php?${params.toString()}`);
    } catch (e) {
      console.warn('[ApiClient] Failed to get vod details:', e);
      return null;
    }
  }

  public async saveProfiles(profiles: UserProfile[]): Promise<boolean> {
    if (!this.isAuthenticated()) return false;
    try {
      const res = await this.request('sync.php?action=save_profiles', {
        method: 'POST',
        body: { profiles },
      });
      return res.success;
    } catch {
      return false;
    }
  }

  public async deleteProfile(profileId: string): Promise<boolean> {
    if (!this.isAuthenticated()) return false;
    try {
      const res = await this.request('sync.php?action=delete_profile', {
        method: 'POST',
        body: { profile_id: profileId },
      });
      return !!(res && res.success);
    } catch {
      return false;
    }
  }

  public async savePlaylists(playlists: PlaylistSource[]): Promise<boolean> {
    if (!this.isAuthenticated()) return false;
    try {
      const res = await this.request('sync.php?action=save_playlists', {
        method: 'POST',
        body: { playlists },
      });
      return res.success;
    } catch {
      return false;
    }
  }

  public async saveSyncData(profileId: string, dataType: 'favorites' | 'history' | 'categories' | 'settings' | 'watched_ids' | 'favorite_categories' | 'channel_overrides', payload: any, clientTs?: number): Promise<boolean> {
    if (!this.isAuthenticated()) return false;
    try {
      const res = await this.request('sync.php?action=save_sync_data', {
        method: 'POST',
        body: clientTs ? { profileId, dataType, payload, clientTs } : { profileId, dataType, payload },
      });
      return res.success;
    } catch {
      return false;
    }
  }

  public async clearHistory(profileId: string, clearWatched = false): Promise<boolean> {
    if (!this.isAuthenticated()) return false;
    try {
      const res = await this.request('sync.php?action=clear_history', {
        method: 'POST',
        body: { profileId, clear_watched: clearWatched },
      });
      return !!(res && res.success);
    } catch {
      return false;
    }
  }

  public async removeWatchedIds(profileId: string, ids: string[]): Promise<boolean> {
    if (!this.isAuthenticated() || !ids || ids.length === 0) return false;
    try {
      const res = await this.request('sync.php?action=remove_watched_ids', {
        method: 'POST',
        body: { profileId, ids },
      });
      return !!(res && res.success);
    } catch {
      return false;
    }
  }

  public async getSyncData(profileId?: string): Promise<{ success: boolean; profileId?: string; history?: any[]; watchedIds?: string[]; watched_ids?: string[]; favorites?: any[] } | null> {
    if (!this.isAuthenticated()) return null;
    try {
      const qs = profileId ? `&profile_id=${encodeURIComponent(profileId)}` : '';
      return await this.request(`sync.php?action=get_sync_data${qs}`);
    } catch (e) {
      console.warn('[ApiClient] Failed to get lightweight sync data:', e);
      return null;
    }
  }

  public async cleanupOrphanData(): Promise<{ success: boolean; message?: string; deleted_count?: number }> {
    if (!this.isAuthenticated()) return { success: false, error: 'Não autenticado' } as any;
    try {
      const res = await this.request('admin.php?action=cleanup_orphan_data', {
        method: 'POST',
      });
      return res;
    } catch (e: any) {
      return { success: false, error: e.message } as any;
    }
  }

  // 2.5 CATÁLOGO CENTRALIZADO NO MYSQL (ISOLADO POR LISTA / SERVIDOR)
  public async saveGlobalCatalog(
    catalog: { channels?: any[]; movies?: any[]; series?: any[]; playlists?: any[]; categoryConfig?: any; replaceVod?: boolean },
    playlistId?: string
  ): Promise<boolean> {
    try {
      let resolvedPlId = playlistId || '';
      if (!resolvedPlId && catalog.playlists && catalog.playlists.length > 0) {
        resolvedPlId = catalog.playlists[0].id || '';
      }
      if (!resolvedPlId) {
        try {
          const storedPls = localStorage.getItem('nexus_iptv_playlists');
          if (storedPls) {
            const parsed = JSON.parse(storedPls);
            if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.id) {
              resolvedPlId = parsed[0].id;
            }
          }
        } catch {}
      }

      const storageKey = resolvedPlId ? `iptv_catalog_cache_${resolvedPlId}` : 'iptv_global_catalog_cache';
      try {
        localStorage.setItem(storageKey, JSON.stringify(catalog));
      } catch {
        // Ignora estouro de quota do localStorage do navegador
      }

      const res = await this.request('sync.php?action=save_global_catalog', {
        method: 'POST',
        body: { catalog, playlist_id: resolvedPlId },
      });
      return !!(res && res.success);
    } catch (e: any) {
      console.error('[ApiClient] Failed to save global catalog to MySQL:', e);
      throw new Error(e.message || 'Falha ao salvar no banco de dados');
    }
  }

  public async loadGlobalCatalog(playlistId?: string): Promise<{ channels?: any[]; movies?: any[]; series?: any[]; playlists?: any[]; channelProfiles?: Record<string, ChannelStreamProfile> } | null> {
    try {
      const url = playlistId 
        ? `sync.php?action=load_global_catalog&playlist_id=${encodeURIComponent(playlistId)}`
        : 'sync.php?action=load_global_catalog';
      const res = await this.request(url);
      if (res && res.success && res.catalog) {
        if (res.channelProfiles) {
          try {
            localStorage.setItem('iptv_channel_stream_profiles', JSON.stringify(res.channelProfiles));
          } catch {}
          res.catalog.channelProfiles = res.channelProfiles;
        }
        const storageKey = playlistId ? `iptv_catalog_cache_${playlistId}` : 'iptv_global_catalog_cache';
        try {
          localStorage.setItem(storageKey, JSON.stringify(res.catalog));
        } catch {
          // Ignora estouro de quota
        }
        return res.catalog;
      }
    } catch (e) {
      console.warn('[ApiClient] Failed to load global catalog from MySQL:', e);
    }
    try {
      const storageKey = playlistId ? `iptv_catalog_cache_${playlistId}` : 'iptv_global_catalog_cache';
      const cached = localStorage.getItem(storageKey) || localStorage.getItem('iptv_global_catalog_cache');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }

  public async getChannelProfiles(): Promise<Record<string, ChannelStreamProfile>> {
    try {
      const res = await this.request('sync.php?action=get_channel_profiles');
      if (res && res.success && res.channelProfiles) {
        try {
          localStorage.setItem('iptv_channel_stream_profiles', JSON.stringify(res.channelProfiles));
        } catch {}
        return res.channelProfiles;
      }
    } catch (e) {
      console.warn('[ApiClient] Failed to load channel profiles:', e);
    }
    try {
      const cached = localStorage.getItem('iptv_channel_stream_profiles');
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  }

  public async saveChannelProfile(data: {
    channelId: string;
    channelName?: string;
    preferredPlayer: string;
    preferredFormat?: string;
    sourceUrl?: string;
    deviceId?: string;
  }): Promise<boolean> {
    try {
      // Salva no cache local imediatamente
      try {
        const cached = JSON.parse(localStorage.getItem('iptv_channel_stream_profiles') || '{}');
        cached[data.channelId] = {
          preferred_player: data.preferredPlayer,
          preferred_format: data.preferredFormat || 'auto',
          updated_at: new Date().toISOString(),
        };
        localStorage.setItem('iptv_channel_stream_profiles', JSON.stringify(cached));
      } catch {}

      const res = await this.request('sync.php?action=save_channel_profile', {
        method: 'POST',
        body: JSON.stringify({
          channel_id: data.channelId,
          channel_name: data.channelName,
          preferred_player: data.preferredPlayer,
          preferred_format: data.preferredFormat || 'auto',
          source_url: data.sourceUrl,
          device_id: data.deviceId,
        }),
      });
      return !!(res && res.success);
    } catch (e) {
      console.warn('[ApiClient] Failed to save channel profile:', e);
      return false;
    }
  }

  // 3. ADMIN: GESTÃO DE MULTI-DNS (ATÉ 6 DNSs)
  public async getDNSList(): Promise<ServerDNS[]> {
    const DEFAULT_DNS: ServerDNS[] = [
      // V9.0.2: nenhum servidor fixo no código (a DNS para tocar vem da lista do cliente)
      { id: 1, name: 'DNS 1', url: '', priority: 1, isActive: false },
    ];

    try {
      const data = await this.request('admin.php?action=get_dns');
      if (data.success && Array.isArray(data.dns) && data.dns.length > 0) {
        localStorage.setItem(DNS_STORAGE_KEY, JSON.stringify(data.dns));
        return data.dns;
      }
    } catch (e) {
      console.warn('[ApiClient] Failed to fetch server DNS from API, falling back to local:', e);
    }

    const saved = localStorage.getItem(DNS_STORAGE_KEY);
    // V8.1.9: sem resposta do servidor e sem lista salva, NENHUM servidor fica ativo por padrão —
    // os players mantêm o servidor do próprio item. A lista vale só como modelo para o painel.
    return saved ? JSON.parse(saved) : DEFAULT_DNS.map((d) => ({ ...d, isActive: false }));
  }

  public async saveDNSList(dns: ServerDNS[]): Promise<boolean> {
    localStorage.setItem(DNS_STORAGE_KEY, JSON.stringify(dns));
    try {
      const res = await this.request('admin.php?action=save_dns', {
        method: 'POST',
        body: { dns },
      });
      return res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to save DNS to remote MySQL:', e);
      return true; // Local saved
    }
  }

  // 4. ADMIN: GESTÃO DE CLIENTES
  public async getClientsList(): Promise<SystemClient[]> {
    try {
      const data = await this.request('admin.php?action=list_clients');
      if (data && data.success && Array.isArray(data.clients)) {
        return data.clients;
      }
      return [];
    } catch (e) {
      console.error('[ApiClient] Failed to fetch clients from API:', e);
      return [];
    }
  }

  public async saveClient(client: Partial<SystemClient>): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=save_client', {
        method: 'POST',
        body: client,
      });
      if (res && res.success) {
        return true;
      }
      throw new Error(res?.error || 'Falha ao salvar cliente no servidor.');
    } catch (e: any) {
      console.error('[ApiClient] Failed to save client to remote API:', e);
      throw e;
    }
  }

  public async deleteClient(id: number): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=delete_client', {
        method: 'POST',
        body: { id },
      });
      if (res && res.success) {
        return true;
      }
      throw new Error(res?.error || 'Falha ao excluir cliente no servidor.');
    } catch (e: any) {
      console.error('[ApiClient] Failed to delete client via API:', e);
      throw e;
    }
  }

  // 5. ADMIN: GESTÃO DE MÚLTIPLAS LISTAS COM MULTI-DNS
  public async getPlaylistsList(): Promise<any[]> {
    try {
      const res = await this.request('admin.php?action=list_playlists');
      if (res.success && Array.isArray(res.playlists)) {
        return res.playlists;
      }
    } catch (e) {
      console.warn('[ApiClient] Failed to list playlists from API:', e);
    }
    return [];
  }

  public async saveRemotePlaylist(playlist: any): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=save_playlist', {
        method: 'POST',
        body: playlist,
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to save playlist to remote API:', e);
      return false;
    }
  }

  public async deleteRemotePlaylist(id: string): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=delete_playlist', {
        method: 'POST',
        body: { id },
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to delete playlist via API:', e);
      return false;
    }
  }

  // 6. ADMIN: TELEMETRIA E MANUTENÇÃO DO BANCO MYSQL
  public async getDbTelemetry(): Promise<any | null> {
    try {
      const res = await this.request('admin.php?action=get_db_telemetry');
      if (res.success && res.telemetry) {
        return res.telemetry;
      }
    } catch (e) {
      console.warn('[ApiClient] Failed to fetch database telemetry:', e);
    }
    return null;
  }

  public async optimizeDatabase(): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=optimize_db');
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to optimize database:', e);
      return false;
    }
  }

  // ==========================================
  // 7. MULTI-TENANT: POOL DE CREDENCIAIS POR LISTA
  // ==========================================

  public async getPlaylistCredentials(playlistId?: string): Promise<PlaylistCredential[]> {
    try {
      const url = playlistId
        ? `admin.php?action=list_credentials&playlist_id=${encodeURIComponent(playlistId)}`
        : 'admin.php?action=list_credentials';
      const res = await this.request(url);
      if (res.success && Array.isArray(res.credentials) && res.credentials.length > 0) {
        try {
          localStorage.setItem('cached_playlist_credentials', JSON.stringify(res.credentials));
        } catch {}
        return res.credentials;
      }
      // Se retornou lista vazia mas temos cache local anterior, usa o cache de segurança
      if (res.success && Array.isArray(res.credentials) && res.credentials.length === 0) {
        const cached = localStorage.getItem('cached_playlist_credentials');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
          } catch {}
        }
      }
      return res.credentials || [];
    } catch (e) {
      console.warn('[ApiClient] Failed to fetch playlist credentials, trying cache:', e);
      try {
        const cached = localStorage.getItem('cached_playlist_credentials');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
      } catch {}
    }
    return [];
  }

  // Testa AO VIVO cada conta ativa do pool direto no provedor (login, status, conexões em uso).
  // Ação só de leitura — não muda nada no banco nem nas contas.
  public async checkPoolHealth(): Promise<{
    checked_at: string;
    summary: { total_checked: number; healthy: number; invalid: number; at_capacity: number; unreachable: number };
    results: Array<{
      id: number;
      label: string;
      status: 'healthy' | 'invalid' | 'at_capacity' | 'unreachable';
      message: string;
      provider_status?: string;
      active_connections?: number;
      max_connections_provider?: number;
      exp_date_provider?: string | null;
    }>;
  } | null> {
    try {
      const res = await this.request('admin.php?action=check_pool_health');
      if (res.success) return res;
      return null;
    } catch (e) {
      console.warn('[ApiClient] Failed to check pool health:', e);
      return null;
    }
  }

  // V9.3.5: Conferir o catálogo com o provedor (só leitura) e tirar o que não existe mais lá (com backup).
  public async conferirCatalogoProvedor(tipo: 'channels' | 'movies' | 'series'): Promise<any> {
    try {
      return await this.request(`catalogo_provedor.php?action=conferir&tipo=${tipo}`);
    } catch (e: any) {
      return { success: false, error: e?.data?.error || e?.message || 'Falha ao conferir.' };
    }
  }

  public async excluirFaltandoNoProvedor(tipo: 'channels' | 'movies' | 'series', ids: string[]): Promise<any> {
    try {
      return await this.request('catalogo_provedor.php?action=excluir', { method: 'POST', body: { tipo, ids } });
    } catch (e: any) {
      return { success: false, error: e?.data?.error || e?.message || 'Falha ao excluir.' };
    }
  }

  public async restoreDefaultCredentials(): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=restore_default_credentials', {
        method: 'POST',
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to restore default credentials:', e);
      return false;
    }
  }

  public async savePlaylistCredential(data: Partial<PlaylistCredential>): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=save_credential', {
        method: 'POST',
        body: data,
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to save credential:', e);
      return false;
    }
  }

  public async renewPlaylistCredential(id: number, days = 30): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=renew_credential', {
        method: 'POST',
        body: { id, days },
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to renew credential:', e);
      return false;
    }
  }

  public async deletePlaylistCredential(id: number): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=delete_credential', {
        method: 'POST',
        body: { id },
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to delete credential:', e);
      return false;
    }
  }

  public async assignCredentialToClient(userId: number, credentialId: number, remove = false): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=assign_credential', {
        method: 'POST',
        body: { user_id: userId, credential_id: credentialId, remove },
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to assign credential:', e);
      return false;
    }
  }

  // ==========================================
  // 8. MULTI-TENANT: GESTÃO DE ASSINATURAS
  // ==========================================

  public async getSubscriptions(userId?: number): Promise<ClientSubscription[]> {
    try {
      const url = userId
        ? `admin.php?action=list_subscriptions&user_id=${userId}`
        : 'admin.php?action=list_subscriptions';
      const res = await this.request(url);
      if (res.success && Array.isArray(res.subscriptions)) {
        return res.subscriptions;
      }
    } catch (e) {
      console.warn('[ApiClient] Failed to fetch subscriptions:', e);
    }
    return [];
  }

  public async saveSubscription(data: Partial<ClientSubscription>): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=save_subscription', {
        method: 'POST',
        body: data,
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to save subscription:', e);
      return false;
    }
  }

  public async checkExpiredSubscriptions(): Promise<any> {
    try {
      const res = await this.request('admin.php?action=check_expired_subscriptions');
      return res;
    } catch (e) {
      console.warn('[ApiClient] Failed to check expired subscriptions:', e);
      return null;
    }
  }

  // ==========================================
  // 9. HEARTBEAT: CONTROLE DE TELAS ATIVAS (Entry/Exit)
  // ==========================================

  public async registerScreen(
    deviceId: string,
    profileId: string,
    contentType: 'live' | 'movie' | 'series' | 'idle' = 'idle',
    contentName = '',
    streamRoute?: 'fusion' | 'oracle',
    profileName?: string
  ): Promise<HeartbeatResponse | null> {
    try {
      const res = await this.request('heartbeat.php?action=register', {
        method: 'POST',
        body: {
          device_id: deviceId,
          profile_id: profileId,
          profile_name: profileName,
          content_type: contentType,
          content_name: contentName,
          stream_route: streamRoute || 'oracle',
          // V8.8.6: este app sabe perguntar "Seu perfil está reproduzindo em outra tela (TV)"
          cast_aware: 1,
        },
      });
      return res as HeartbeatResponse;
    } catch (e) {
      console.warn('[ApiClient] Failed to register screen:', e);
      return null;
    }
  }

  /** V8.8.6: "Assistir aqui e fechar a outra tela" — para a TV (Cast) que toca para este perfil. */
  public async releaseProfileTv(profileId: string): Promise<boolean> {
    try {
      const res = await this.request('tv_sync.php?action=release_profile', {
        method: 'POST',
        body: { profile_id: profileId },
      });
      return !!res?.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to release profile TV:', e);
      return false;
    }
  }

  public async unregisterScreen(deviceId: string, contentName?: string): Promise<boolean> {
    try {
      const res = await this.request('heartbeat.php?action=unregister', {
        method: 'POST',
        body: { device_id: deviceId, content_name: contentName },
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to unregister screen:', e);
      return false;
    }
  }

  public async disconnectScreen(
    targetDeviceId: string,
    screenId?: number,
    kickerProfileName?: string,
    callerData?: {
      callerDeviceId?: string;
      callerProfileId?: string;
      callerContentType?: string;
      callerContentName?: string;
      callerStreamRoute?: 'oracle' | 'fusion';
    }
  ): Promise<HeartbeatResponse | null> {
    try {
      const res = await this.request('heartbeat.php?action=disconnect_screen', {
        method: 'POST',
        body: {
          device_id: targetDeviceId,
          target_device_id: targetDeviceId,
          screen_id: screenId,
          id: screenId,
          kicker_profile_name: kickerProfileName,
          caller_device_id: callerData?.callerDeviceId,
          caller_profile_id: callerData?.callerProfileId,
          caller_content_type: callerData?.callerContentType,
          caller_content_name: callerData?.callerContentName,
          caller_stream_route: callerData?.callerStreamRoute,
        },
      });
      return res as HeartbeatResponse;
    } catch (e) {
      console.warn('[ApiClient] Failed to disconnect screen:', e);
      return null;
    }
  }

  public async sendHeartbeat(
    deviceId: string,
    profileId: string,
    contentType?: string,
    contentName?: string,
    streamRoute?: 'fusion' | 'oracle'
  ): Promise<HeartbeatResponse | null> {
    try {
      const res = await this.request('heartbeat.php?action=heartbeat', {
        method: 'POST',
        body: {
          device_id: deviceId,
          profile_id: profileId,
          content_type: contentType,
          content_name: contentName,
          stream_route: streamRoute || 'oracle',
        },
      });
      return res as HeartbeatResponse;
    } catch (e) {
      console.warn('[ApiClient] Failed to send heartbeat:', e);
      return null;
    }
  }

  public async getScreenStatus(): Promise<HeartbeatResponse | null> {
    try {
      const res = await this.request('heartbeat.php?action=status');
      return res as HeartbeatResponse;
    } catch (e) {
      console.warn('[ApiClient] Failed to get screen status:', e);
      return null;
    }
  }

  // ==========================================
  // 10. ADMIN: MONITOR DE TELAS ATIVAS
  // ==========================================

  public async getActiveScreens(): Promise<any> {
    try {
      const res = await this.request('admin.php?action=get_active_screens');
      if (res.success) return res;
    } catch (e) {
      console.warn('[ApiClient] Failed to get active screens:', e);
    }
    return { total_active_screens: 0, total_unique_users: 0, by_user: [], all_screens: [] };
  }

  public async kickScreen(screenId: number): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=kick_screen', {
        method: 'POST',
        body: { screen_id: screenId },
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to kick screen:', e);
      return false;
    }
  }

  public async kickIp(ipAddress: string): Promise<boolean> {
    try {
      const res = await this.request('admin.php?action=kick_ip', {
        method: 'POST',
        body: { ip_address: ipAddress },
      });
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to kick IP:', e);
      return false;
    }
  }

  // ==========================================
  // 11. SESSÕES DE LOGIN (Controle de Locais de Acesso)
  // ==========================================

  public async listSessions(): Promise<any[]> {
    try {
      const res = await this.request('sessions.php?action=list');
      if (res.success && Array.isArray(res.sessions)) return res.sessions;
    } catch (e) {
      console.warn('[ApiClient] Failed to list sessions:', e);
    }
    return [];
  }

  public async revokeSession(sessionId: number): Promise<boolean> {
    try {
      const res = await this.request('sessions.php?action=revoke', {
        method: 'POST',
        body: { session_id: sessionId },
      });
      if (res && res.new_token && this.user) {
        this.setSession(res.new_token, this.user, this.sessionToken || undefined, this.sessionId || undefined);
      }
      return !!res.success;
    } catch (e) {
      console.warn('[ApiClient] Failed to revoke session:', e);
      return false;
    }
  }

  public async revokeAllOtherSessions(): Promise<{ success: boolean; revoked_count: number }> {
    try {
      const res = await this.request('sessions.php?action=revoke_all', {
        method: 'POST',
        body: { session_token: this.sessionToken || '' },
      });
      if (res && res.new_token && this.user) {
        this.setSession(res.new_token, this.user, this.sessionToken || undefined, this.sessionId || undefined);
      }
      return { success: !!res.success, revoked_count: res.revoked_count || 0 };
    } catch (e) {
      console.warn('[ApiClient] Failed to revoke all sessions:', e);
      return { success: false, revoked_count: 0 };
    }
  }

  public async updateSessionActivity(): Promise<void> {
    if (!this.sessionToken || !this.isAuthenticated()) return;
    try {
      await this.request('sessions.php?action=update_activity', {
        method: 'POST',
        body: { session_token: this.sessionToken },
      });
    } catch {
      // Silencioso
    }
  }

  public async claimProfile(profileId: string, deviceId: string, deviceName?: string): Promise<boolean> {
    if (!this.isAuthenticated() || !profileId || !deviceId) return false;
    try {
      const res = await this.request('heartbeat.php?action=claim_profile', {
        method: 'POST',
        body: { profile_id: profileId, device_id: deviceId, device_name: deviceName },
      });
      return !!res?.success;
    } catch {
      return false;
    }
  }

  public async checkProfileActive(profileId: string, deviceId: string): Promise<boolean> {
    if (!this.isAuthenticated() || !profileId || !deviceId) return true;
    try {
      const res = await this.request('heartbeat.php?action=check_profile', {
        method: 'POST',
        body: { profile_id: profileId, device_id: deviceId },
      });
      return !!res?.success;
    } catch {
      return false;
    }
  }

  public async getAdminSessions(): Promise<any> {
    try {
      const res = await this.request('sessions.php?action=admin_list');
      if (res.success) return res;
    } catch (e) {
      console.warn('[ApiClient] Failed to get admin sessions:', e);
    }
    return { total_active_sessions: 0, total_unique_users: 0, by_user: [], all_sessions: [] };
  }

  // ==========================================
  // V8.7.5: AVISOS (api/avisos.php) — mensagem do Gerenciador depois de escolher o perfil
  // ==========================================
  async avisosPendentes(profileId: string, plataforma: 'stick' | 'web', soImediatos?: boolean): Promise<{ avisos: any[]; marca?: number }> {
    const params: Record<string, string> = { profile_id: profileId, plataforma };
    if (soImediatos) params.so_imediatos = '1';
    return this.request('avisos.php?action=pendentes', { params });
  }

  // V8.7.6: só a "marca" dos avisos imediatos (um número) — o Web pergunta a cada 5 min
  async avisosMarca(plataforma: 'stick' | 'web'): Promise<{ marca?: number }> {
    return this.request('avisos.php?action=marca', { params: { plataforma } });
  }

  async avisoMarcar(avisoId: number, profileId: string, acao: 'ok' | 'lembrar', plataforma: 'stick' | 'web'): Promise<any> {
    return this.request('avisos.php?action=marcar', { method: 'POST', body: { aviso_id: avisoId, profile_id: profileId, acao, plataforma } });
  }

  // V8.7.5: Avisos — Gerenciador (só admin)
  async avisosListar(): Promise<{ avisos: any[] }> {
    return this.request('avisos.php?action=listar');
  }

  async avisoSalvar(dados: any): Promise<{ id: number }> {
    return this.request('avisos.php?action=salvar', { method: 'POST', body: dados });
  }

  async avisoAtivar(id: number, ativo: boolean): Promise<any> {
    return this.request('avisos.php?action=ativar', { method: 'POST', body: { id, ativo } });
  }

  // V8.7.6: excluir de vez
  async avisoExcluir(id: number): Promise<any> {
    return this.request('avisos.php?action=excluir', { method: 'POST', body: { id } });
  }

  async avisoRelatorio(id: number): Promise<{ vistos: any[] }> {
    return this.request('avisos.php?action=relatorio', { params: { id: String(id) } });
  }

  // ==========================================
  // V8.7.2: APARELHOS (sticks) — log e comandos a distância (api/stick.php, só admin)
  // ==========================================
  async stickDevices(): Promise<{ aparelhos: any[] }> {
    return this.request('stick.php?action=stick_devices');
  }

  async stickLogs(aparelho: string, opts: { nivel?: string; desde?: string; ate?: string; limite?: number } = {}): Promise<{ logs: any[] }> {
    const params: Record<string, string> = { aparelho };
    if (opts.nivel) params.nivel = opts.nivel;
    if (opts.desde) params.desde = opts.desde;
    if (opts.ate) params.ate = opts.ate;
    if (opts.limite) params.limite = String(opts.limite);
    return this.request('stick.php?action=stick_logs', { params });
  }

  async stickSend(aparelho: string, comando: string, args?: any): Promise<{ id: number }> {
    return this.request('stick.php?action=stick_send', { method: 'POST', body: { aparelho, comando, args } });
  }

  /** V9.2.4: endereço FINAL do filme/série (o servidor pede como player; o navegador toca direto do servidor de vídeo) */
  async resolverVod(url: string): Promise<string | null> {
    try {
      const r: any = await this.request('vod_link.php?action=resolver', { method: 'POST', body: { url } });
      return r && r.success && typeof r.url === 'string' ? r.url : null;
    } catch {
      return null;
    }
  }

  /** V9.0.7: nome fixo do stick (Stick01, Stick02...) = usuário da conta padrão dele */
  async stickRenomear(aparelho: string, nome: string): Promise<{ nome: string; padrao_existe: number }> {
    return this.request('stick.php?action=stick_renomear', { method: 'POST', body: { aparelho, nome } });
  }

  /** V9.0.7: contas de cliente para escolher na troca de conta do stick (só nome e situação) */
  async stickContas(): Promise<{ contas: { username: string; status: string | null; tem_lista: number | string }[] }> {
    return this.request('stick.php?action=stick_contas');
  }

  /** V8.7.3: sobe a imagem da mensagem na tela (data URL) e devolve o endereço dela no servidor */
  async stickUpload(dataUrl: string): Promise<{ url: string }> {
    return this.request('stick.php?action=stick_upload', { method: 'POST', body: { dados: dataUrl } });
  }

  async stickCommands(aparelho: string): Promise<{ comandos: any[] }> {
    return this.request('stick.php?action=stick_commands', { params: { aparelho } });
  }

  // ==========================================
  // 12. TELEMETRIA, AUDITORIA & HISTÓRICO DE REPRODUÇÃO
  // ==========================================

  public async getTelemetryStats(): Promise<TelemetryStats | null> {
    try {
      const res = await this.request('telemetry.php?action=stats');
      if (res.success && res.stats) {
        return res.stats as TelemetryStats;
      }
    } catch (e) {
      console.warn('[ApiClient] Failed to fetch telemetry stats:', e);
    }
    return null;
  }

  public async getPlaybackLogs(params?: {
    user_id?: number;
    profile_id?: string;
    content_type?: 'live' | 'movie' | 'series';
    stream_route?: 'oracle' | 'fusion' | 'all';
    date_range?: 'today' | 'yesterday' | '7days' | '30days' | 'all';
    status?: 'playing' | 'stopped' | 'error';
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; logs: PlaybackLogItem[]; route_summary?: RouteSummary }> {
    try {
      const query = new URLSearchParams();
      if (params?.user_id) query.set('user_id', String(params.user_id));
      if (params?.profile_id) query.set('profile_id', params.profile_id);
      if (params?.content_type) query.set('content_type', params.content_type);
      if (params?.stream_route && params.stream_route !== 'all') query.set('stream_route', params.stream_route);
      if (params?.date_range) query.set('date_range', params.date_range);
      if (params?.status) query.set('status', params.status);
      if (params?.search) query.set('search', params.search);
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.offset) query.set('offset', String(params.offset));

      const res = await this.request(`telemetry.php?action=playback_logs&${query.toString()}`);
      if (res.success && Array.isArray(res.logs)) {
        return { total: res.total || 0, logs: res.logs, route_summary: res.route_summary };
      }
    } catch (e) {
      console.warn('[ApiClient] Failed to fetch playback logs:', e);
    }
    return { total: 0, logs: [] };
  }

  public async getTelemetryErrors(params?: {
    event_type?: string;
    severity?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; errors: TelemetryErrorItem[] }> {
    try {
      const query = new URLSearchParams();
      if (params?.event_type) query.set('event_type', params.event_type);
      if (params?.severity) query.set('severity', params.severity);
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.offset) query.set('offset', String(params.offset));

      const res = await this.request(`telemetry.php?action=telemetry_errors&${query.toString()}`);
      if (res.success && Array.isArray(res.errors)) {
        return { total: res.total || 0, errors: res.errors };
      }
    } catch (e) {
      console.warn('[ApiClient] Failed to fetch telemetry errors:', e);
    }
    return { total: 0, errors: [] };
  }

  public async reportTelemetryError(data: {
    event_type: string;
    severity?: 'info' | 'warning' | 'error' | 'critical';
    profile_id?: string;
    content_type?: string;
    content_name?: string;
    stream_url?: string;
    error_message: string;
    device_id?: string;
    device_info?: string;
  }): Promise<boolean> {
    try {
      const res = await this.request('telemetry.php?action=report_error', {
        method: 'POST',
        body: data,
      });
      return !!res.success;
    } catch {
      return false;
    }
  }

  public async clearOldTelemetryLogs(days = 60): Promise<boolean> {
    try {
      const res = await this.request('telemetry.php?action=clear_old_logs', {
        method: 'POST',
        body: { days },
      });
      return !!res.success;
    } catch {
      return false;
    }
  }
}

export const apiClient = new ApiClient();
